import { inject, Injectable } from '@angular/core';
import { NetworkProvider } from '../network/network';
import { LoggerWrapper } from '../logger/logger-wrapper.service';
import { LoggerFactory } from '../logger/logger-factory.service';
import { LoaderService } from 'src/app/shared/providers/loading.service';
import { InventaireEquipementPhotoResponse } from '../../models/inventaire.model';
import { DocumentControllerV2Api } from '../../../api/services/document-controller-v-2-api';
import { GetDocumentMetadataById$Params } from '../../../api/fn/document-controller-v-2/get-document-metadata-by-id';
import { firstValueFrom } from 'rxjs';
import { DocumentMetaDataDto } from '../../../api/models/document-meta-data-dto';
import { DeleteDocumentById$Params } from '../../../api/fn/document-controller-v-2/delete-document-by-id';
import { GetDocumentContentById$Params } from '../../../api/fn/document-controller-v-2/get-document-content-by-id';
import { UpsertDocumentRequest } from '../../../api/models/upsert-document-request';
import { UpdateDocument$Params } from '../../../api/fn/document-controller-v-2/update-document';
import { LoadingController, ToastController } from '@ionic/angular';
import { DocumentContentDto } from '../../../api/models/document-content-dto';

@Injectable({ providedIn: 'root' })
export class DocumentsProvider {
  private loaderService = inject(LoaderService);
  private networkProvider = inject(NetworkProvider);
  private loggerFactory = inject(LoggerFactory);
  private documentApi: DocumentControllerV2Api = inject(DocumentControllerV2Api);
  private toastController: ToastController = inject(ToastController);
  private loadingCtrl: LoadingController = inject(LoadingController);

  private logger: LoggerWrapper;

  constructor() {
    this.logger = this.loggerFactory.getInstance(this);
  }

  /**
   *  Recuperer les details d'un document / photo
   */
  async getDocumentMetadata(id_document: string): Promise<DocumentMetaDataDto> {
    try {
      const params: GetDocumentMetadataById$Params = {
        idDocument: id_document,
      };
      const data: DocumentMetaDataDto = await firstValueFrom(this.documentApi.getDocumentMetadataById(params));
      this.logger.info('Les métadonnées du document ont été récupérées', data);
      return data;
    } catch (err) {
      this.logger.error(err, "Une erreur s'est produite lors de la récupération des métadonnées du document");
      return Promise.reject(
        `Une erreur s'est produite lors de la récupération des métadonnées du document : ${id_document}`
      );
    }
  }

  /**
   * Suppression de la photo
   */
  async deleteDocument(id_document: string) {
    const loading = await this.loadingCtrl.create({
      message: 'Suppression en cours',
    });
    await loading.present();
    const params: DeleteDocumentById$Params = {
      idDocument: id_document,
    };
    firstValueFrom(this.documentApi.deleteDocumentById(params))
      .then((data) => {
        loading.dismiss();
        return data;
      })
      .catch((err) => {
        this.logger.warn('error deletePicture', err);
        return false;
      });
  }

  /**
   * Récupération du contenu de photos
   */
  async loadPictures(data: InventaireEquipementPhotoResponse[]) {
    // this.logger.log("loadPictures",data);
    let pictures = [];
    if (data && data.length > 0)
      for (let i = 0; i < data.length; i++) {
        // this.logger.log("data["+ i + "] : ", data[i]);
        await this.getContent(data[i].id_document)
          .then((content) => {
            pictures.push({
              id_document: data[i].id_document,
              metadata: {
                nom: data[i].nom,
                description: data[i].description ? data[i].description : '',
                type: data[i].mime,
              },
              contenu: content.contenu,
            });
          })
          .catch((err) => {
            this.logger.warn('error getPictures', err);
          });
      }
    return pictures;
  }

  /**
   * Récupère le contenu d'un document en base 64
   *
   * @param {string} id_document  id du document
   * @returns {Promise<string>} Promesse qui se résoud en string base 64
   */
  async getContent(id_document: string): Promise<DocumentContentDto> {
    try {
      const params: GetDocumentContentById$Params = {
        idDocument: id_document,
      };
      return await firstValueFrom(this.documentApi.getDocumentContentById(params));
    } catch (ex) {
      this.logger.error(ex, `error getContent for ${id_document}`);
      throw ex;
    }
  }

  /**
   * Remplace un fichier qui était associé à une localisation
   *
   * @param {string} id  id du document à remplacer
   * @param {any} file fichier à envoyer
   */
  async putDocument(file: UpsertDocumentRequest, id: string) {
    const params: UpdateDocument$Params = {
      idDocument: id,
      body: file,
    };
    if (await this.networkProvider.isReallyConnected()) {
      try {
        await firstValueFrom(this.documentApi.updateDocument(params));
      } catch (ex) {
        this.logger.error(ex, `Cannot put online document with id ${id}`);
      }
    } else {
      await this.toastController.create({
        message: 'Vous êtes actuellement hors-ligne. Veuillez réessayer ultérieurement.',
        duration: 5000,
      });
    }
  }
}
